/**
 * Signature Service - CRUD Operations and Sync for Document Signatures
 *
 * This service manages document signatures for the offline-first signing flow.
 * Signatures are collected locally and synced to the server when online.
 */

import db from '@/db';
import { ClientSignatureRepresentative, Signature, SignatureSubmission, getSignatureSlotId, scopedLocalId, slotIdFromLocalId } from '../types/signature';
import { MONAMI_HOST } from '../../config/env';
import { getCurrentPerson } from './user_service';
import { trackEvent } from './analytics_service';
import { officialFullName } from '../types/person';
import { fetchWithAuth, AuthExpiredError } from '../../lib/fetch-utils';
import { softSignOut } from '../../lib/auth';

import { createLogger, LogLevel } from '../../lib/logger';

const logger = createLogger('SignatureService', LogLevel.DEBUG);
/**
 * Fetch signatures for a document from the server
 * This should be called after a document has been finalized on the server
 */
export async function fetchSignatures(
  clientId: number,
  documentId: number
): Promise<Signature[]> {
  const currentPerson = await getCurrentPerson();
  if (!currentPerson) {
    throw new Error('No current person selected');
  }

  const url = `${MONAMI_HOST}/api/offline/clients/${clientId}/documents/${documentId}/signatures?current_person_id=${currentPerson.id}`;

  const response = await fetchWithAuth(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    timeout: 10000
  }, () => {
    // Auth expired during signature sync — soft cleanup that preserves
    // the local DB. The signature is still on disk and will re-sync after
    // the user re-authenticates.
    softSignOut();
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch signatures: ${response.status}`);
  }

  const data = await response.json();
  const signatures: Signature[] = data.records || data.signatures || data || [];

  // Add document_id and the slot identity to each signature. The server emits
  // local_id as the bare slot; derive it when absent. createSignatures scopes
  // it by document before storage.
  return signatures.map(sig => ({
    ...sig,
    document_id: documentId,
    local_id: sig.local_id || getSignatureSlotId(sig) || `server-${sig.id}`
  }));
}

/**
 * Save signatures to local IndexedDB
 * Signatures must have local_id set before calling this function
 */
export async function createSignatures(
  documentId: number | string,
  signatures: Signature[]
): Promise<void> {
  await db.transaction('rw', db.signatures, async () => {
    // Remove existing signatures for this document
    await db.signatures.where('document_id').equals(documentId).delete();

    // Store under the document-scoped key (local_id is the table's primary key,
    // and bare slots collide across documents sharing a signature config).
    // Accepts rows keyed by slot, by scoped local_id, or with no local_id at all.
    const signaturesWithDocId = signatures.map(sig => {
      const slotId = (sig.local_id && slotIdFromLocalId(sig.local_id)) || getSignatureSlotId(sig) || `server-${sig.id}`;
      return {
        ...sig,
        document_id: documentId,
        local_id: scopedLocalId(documentId, slotId)
      };
    });
    await db.signatures.bulkPut(signaturesWithDocId);
  });
}

/**
 * Get signatures for a document from local IndexedDB
 */
export async function getLocalSignatures(documentId: number | string): Promise<Signature[]> {
  return await db.signatures.where('document_id').equals(documentId).toArray();
}

/**
 * Drop all locally-captured signatures for a document. Used when the server reports
 * the signing ceremony was invalidated (the document was re-opened online), so the
 * stale captures must not be replayed.
 */
export async function deleteLocalSignatures(documentId: number | string): Promise<void> {
  const signatures = await db.signatures.where('document_id').equals(documentId).toArray();

  // Track what was dropped before deleting, so a discarded offline capture is auditable.
  if (signatures.length > 0) {
    trackEvent('document.signatures_discarded', {
      document_id: documentId,
      discarded_signatures: signatures.map(sig => ({
        slot: slotIdFromLocalId(sig.local_id),
        status: sig.status,
        local_completed: sig.local_completed,
        signing_request_id: sig.signing_request_id,
        signatory_id: sig.signatory_id,
        name_confirmation: sig.name_confirmation,
        signed_at: sig.signed_at,
      })),
    }).catch(err => logger.warn('Failed to track document.signatures_discarded', err));
  }

  await db.signatures.where('document_id').equals(documentId).delete();
}

/**
 * Persist server-provided signature rows (from the documents index/show payloads,
 * the finalize response, or the signatures endpoint) into the local signatures
 * table. The server payload replaces the document's rows, with two protections
 * for signing progress still awaiting sync:
 * - a slot present in both sets keeps the local capture until the server
 *   confirms it as completed (a stale payload never clobbers it);
 * - a captured slot ABSENT from the payload is preserved rather than deleted
 *   (e.g. required_people slots, which setup_signatures! does not create
 *   server-side). Absent slots without captured progress follow the server.
 */
export async function storeServerSignatures(
  documentId: number | string,
  serverSignatures: Signature[] | undefined
): Promise<void> {
  if (!Array.isArray(serverSignatures) || serverSignatures.length === 0) return;

  // Merge by slot identity — tolerant of legacy rows stored under bare slots
  const existing = await getLocalSignatures(documentId);
  const existingBySlot = new Map(existing.map(sig => [slotIdFromLocalId(sig.local_id), sig]));

  // A capture belongs to the payload's signing ceremony when either side has no
  // ceremony id (rows created offline get one only after the server finalizes)
  // or the ids match. A re-open invalidates the ceremony; a later finalize
  // starts a new one with the same slots — captures from the old ceremony are
  // stale and must not carry over.
  const payloadRequestId = serverSignatures.find(sig => sig.signing_request_id != null)?.signing_request_id;
  const sameCeremony = (local: Signature) =>
    !local.signing_request_id || !payloadRequestId || local.signing_request_id === payloadRequestId;

  const payloadSlots = new Set<string>();
  const rows = serverSignatures.map(serverSig => {
    const slotId = (serverSig.local_id && slotIdFromLocalId(serverSig.local_id)) || getSignatureSlotId(serverSig) || `server-${serverSig.id}`;
    payloadSlots.add(slotId);
    const local = existingBySlot.get(slotId);

    if (local?.local_completed && serverSig.status !== 'completed' && sameCeremony(local)) return local;

    return {
      ...serverSig,
      document_id: documentId,
      local_id: slotId,
      // A capture from an invalidated ceremony doesn't count as signed anymore
      local_completed: (local != null && local.local_completed && sameCeremony(local)) || false
    };
  });

  const preservedCaptures = existing.filter(sig =>
    !payloadSlots.has(slotIdFromLocalId(sig.local_id)) && sig.local_completed && sig.status !== 'completed' && sameCeremony(sig)
  );

  await createSignatures(documentId, [...rows, ...preservedCaptures]);
}

/**
 * Mark a signature as locally completed
 * This stores the signature data locally until it can be synced to the server
 */
export async function completeSignatureLocally(
  localId: string,
  data: {
    name_confirmation: string;
    signed_at: string;
    signatory_id: number;
    client_signature_representative?: ClientSignatureRepresentative;
    signing_statement?: string | null;
  }
): Promise<Signature | null> {
  const signature = await db.signatures.get(localId);
  if (!signature) {
    throw new Error(`Signature ${localId} not found`);
  }

  const updatedSignature: Signature = {
    ...signature,
    name_confirmation: data.name_confirmation,
    signed_at: data.signed_at,
    signatory_id: data.signatory_id,
    client_signature_representative: data.client_signature_representative,
    signing_statement: data.signing_statement,
    signature_format: 'typed',
    local_completed: true
  };

  await db.signatures.put(updatedSignature);
  return updatedSignature;
}

/**
 * Submit a signature to the server
 */
export async function submitSignature(
  clientId: number,
  documentId: number,
  signatureId: number,
  data: SignatureSubmission
): Promise<Signature> {
  const currentPerson = await getCurrentPerson();
  if (!currentPerson) {
    throw new Error('No current person selected');
  }

  const url = `${MONAMI_HOST}/api/offline/clients/${clientId}/documents/${documentId}/signatures/${signatureId}?current_person_id=${currentPerson.id}`;

  const response = await fetchWithAuth(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({ signature: data }),
    timeout: 10000
  }, () => {
    // Auth expired during signature sync — soft cleanup that preserves
    // the local DB. The signature is still on disk and will re-sync after
    // the user re-authenticates.
    softSignOut();
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { error?: string; errors?: string[]; message?: string; signature?: Signature };
    // 409 signature_completed: the slot was already signed (another device or
    // the online editor). The body carries the authoritative row — adopt it
    // instead of erroring, so the caller stores it and stops retrying.
    if (response.status === 409 && errorData.error === 'signature_completed' && errorData.signature) {
      return {
        ...errorData.signature,
        document_id: documentId
      };
    }
    // Validation failures come back as { errors: [...] } (e.g. a typed name
    // that doesn't match the signatory's official full name)
    const validationMessage = Array.isArray(errorData.errors) ? errorData.errors.join(', ') : undefined;
    throw new Error(validationMessage || errorData.message || `Failed to submit signature: ${response.status}`);
  }

  const updatedSignature = await response.json();
  return {
    ...updatedSignature,
    document_id: documentId
  };
}

/**
 * Expected typed name for a slot: slots with a designated signatory (person
 * slots) expect that person's name; title slots are signed by the current user.
 */
export function expectedSignatureName(sig: Signature, currentPersonName: string): string {
  if (sig.signatory) {
    return officialFullName(sig.signatory);
  }
  if (sig.title) return currentPersonName;
  return '';
}

export interface SignSlotContext {
  online: boolean;
  clientId?: number | null;
  currentPersonId?: number;
  currentPersonName: string;
}

/** Signer identity question answer for the ClientProxy slot: the chosen
 *  representative option and the person it resolves to (the client, the legal
 *  representative, or the current user for the on-behalf options). */
export interface SignSlotOptions {
  representative: ClientSignatureRepresentative;
  signatory: { id: number; name: string };
  signingStatement?: string | null;
}

export type SignSlotResult =
  | { ok: false; error: string }
  | {
      ok: true;
      signedLocally: boolean;
      allSigned: boolean;
    };

/**
 * Sign one slot of a document's Signature tab. Slots the server knows about are
 * PATCHed directly when online (the document status transitions server-side);
 * otherwise the capture is stored locally for the sync layer to replay.
 *
 * The typed name is matched case-insensitively against the expected signer, but
 * the CANONICAL expected name is what gets stored/submitted — the server-side
 * match (Signing::Signature#name_matches) is exact, so persisting the user's
 * raw casing would sign locally yet be rejected during sync.
 */
export async function signSignatureSlot(
  documentId: number | string,
  row: Signature,
  typedName: string,
  context: SignSlotContext,
  options?: SignSlotOptions
): Promise<SignSlotResult> {
  // The signer identity question overrides the slot's implicit signer.
  const expected = (options?.signatory.name ?? expectedSignatureName(row, context.currentPersonName)).trim();
  const trimmedName = typedName.trim();
  if (expected && trimmedName.toLowerCase() !== expected.toLowerCase()) {
    return { ok: false, error: `Signatory name must match "${expected}"` };
  }
  const nameConfirmation = expected || trimmedName;

  const signedAt = new Date().toISOString();
  const signatoryId = options?.signatory.id ?? row.signatory_id ?? row.signatory?.id ?? context.currentPersonId;
  if (!signatoryId) return { ok: false, error: 'Could not determine the signatory. Please try again.' };

  const representativeFields = options && {
    client_signature_representative: options.representative,
    signing_statement: options.signingStatement || null,
  };

  let signedLocally = false;
  try {
    if (context.online && typeof row.id === 'number' && typeof documentId === 'number' && context.clientId) {
      const submitted = await submitSignature(context.clientId, documentId, row.id, {
        name_confirmation: nameConfirmation,
        signed_at: signedAt,
        signatory_id: signatoryId,
        signature_format: 'typed',
        ...representativeFields,
      });
      await db.signatures.put({ ...row, ...submitted, local_id: row.local_id, document_id: documentId, local_completed: true });
    } else {
      await completeSignatureLocally(row.local_id, {
        name_confirmation: nameConfirmation,
        signed_at: signedAt,
        signatory_id: signatoryId,
        ...representativeFields,
      });
      signedLocally = true;
    }
  } catch (err) {
    logger.error('Failed to complete signature', { documentId, localId: row.local_id, error: err });
    return { ok: false, error: err instanceof Error && err.message ? err.message : 'Could not complete the signature. Please try again.' };
  }

  const rows = await getLocalSignatures(documentId);
  const allSigned = rows.length > 0 && rows.every(sig => sig.status === 'completed' || sig.local_completed);
  return { ok: true, signedLocally, allSigned };
}

/**
 * Sync locally completed signatures to the server
 * Note: This only works for signatures that have a server ID.
 * For new local signatures, use the flow in assessment_service which
 * fetches server signatures after finalize and matches by local_id.
 */
export async function syncSignatures(
  clientId: number,
  documentId: number
): Promise<{ synced: number; failed: number }> {
  // Get locally completed but unsynced signatures that have server IDs
  const localSignatures = await db.signatures
    .where('document_id')
    .equals(documentId)
    .filter(sig => sig.local_completed === true && sig.id !== undefined && sig.status !== 'completed')
    .toArray();

  let synced = 0;
  let failed = 0;

  for (const signature of localSignatures) {
    if (!signature.id || !signature.name_confirmation || !signature.signed_at || !signature.signatory_id) {
      logger.warn(`Signature ${signature.local_id} missing required data or server ID, skipping`);
      failed++;
      continue;
    }

    try {
      const submissionData: SignatureSubmission = {
        name_confirmation: signature.name_confirmation,
        signed_at: signature.signed_at,
        signatory_id: signature.signatory_id,
        signature_format: signature.signature_format || 'typed',
        client_signature_representative: signature.client_signature_representative,
        signing_statement: signature.signing_statement
      };

      const updatedSignature = await submitSignature(
        clientId,
        documentId,
        signature.id,
        submissionData
      );

      // Update local record with server response, preserving local_id
      await db.signatures.put({
        ...updatedSignature,
        local_id: signature.local_id,
        document_id: documentId,
        local_completed: true
      });

      synced++;
    } catch (error) {
      if (error instanceof AuthExpiredError) {
        throw error; // Re-throw auth errors
      }
      logger.error(`Failed to sync signature ${signature.local_id}:`, error);
      failed++;
    }
  }

  return { synced, failed };
}

/**
 * Check if all required signatures are completed locally
 */
export async function areAllSignaturesCompleted(documentId: number | string): Promise<boolean> {
  const signatures = await getLocalSignatures(documentId);

  // If no signatures, consider it complete (no signatures required)
  if (signatures.length === 0) {
    return true;
  }

  // Check if all signatures have local_completed or status === 'completed'
  return signatures.every(sig =>
    sig.local_completed === true || sig.status === 'completed'
  );
}

/**
 * Get pending (unsynced) signatures count for a document
 */
export async function getPendingSignaturesCount(documentId: number | string): Promise<number> {
  return await db.signatures
    .where('document_id')
    .equals(documentId)
    .filter(sig => sig.local_completed === true && sig.id === undefined)
    .count();
}
